import { useEffect, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { forceUpdate } from '@/core/version/force-update'
import { logger } from '@/core/logger'
import { APP_VERSION, SENTRY_ENV } from '@/core/sentry'

/**
 * Top-level error boundary. Catches any render error anywhere in the tree,
 * reports it to Sentry, and shows a recovery screen so the app never gets
 * stuck in a half-rendered state (e.g. a modal scrim left on-screen after
 * the dialog content throws — the exact symptom that prompted this).
 *
 * Why Sentry's own ErrorBoundary:
 *  - captures with the React component stack already attached
 *  - ties the error to the active user/scope
 *  - avoids a parallel manual `captureException` path
 *
 * Unhandled `error` + `unhandledrejection` events are captured by the
 * Sentry browser SDK's default integrations — this component only adds
 * coverage for React render errors that never reach `window`.
 */
export function RootErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <Fallback error={error} resetError={resetError} eventId={eventId ?? null} />
      )}
      onError={(error) => {
        logger.e('Root error boundary caught render error', error)
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

function Fallback({
  error,
  resetError,
  eventId,
}: {
  error: unknown
  resetError: () => void
  eventId: string | null
}) {
  // A render crash may have happened while a modal / drawer had body scroll
  // locked — reset it so the recovery UI scrolls normally and any stray
  // scrim class goes away.
  useEffect(() => {
    document.body.style.overflow = ''
    document.documentElement.classList.remove('sidebar-open')
    document.body.classList.remove('sidebar-open')
  }, [])

  const message = error instanceof Error ? error.message : String(error ?? 'Error desconocido')

  const report = () => {
    if (!eventId) return
    Sentry.showReportDialog({ eventId, lang: 'es' })
  }

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'oklch(0.18 0.03 250)',
        color: 'oklch(0.96 0.01 230)',
        fontFamily: 'Inter, system-ui, sans-serif',
        zIndex: 9999,
      }}
    >
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          Algo ha ido mal
        </h1>
        <p style={{ opacity: 0.75, fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          Hemos registrado el error. Reintenta sin recargar, o recarga la app
          para empezar de cero.
        </p>
        <pre
          style={{
            fontSize: 11,
            opacity: 0.5,
            background: 'oklch(0.14 0.02 250)',
            border: '2px solid oklch(0.28 0.02 250)',
            borderRadius: 6,
            padding: '8px 10px',
            marginBottom: 20,
            overflow: 'auto',
            maxHeight: 120,
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
          }}
        >
          {message}
        </pre>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn ghost sm" onClick={resetError}>
            Reintentar
          </button>
          {eventId && (
            <button type="button" className="btn ghost sm" onClick={report}>
              Reportar
            </button>
          )}
          <button
            type="button"
            className="btn primary sm"
            onClick={() => {
              void forceUpdate()
            }}
          >
            Recargar
          </button>
        </div>
        <p style={{ fontSize: 10, opacity: 0.4, marginTop: 16, fontFamily: 'ui-monospace, monospace' }}>
          clubmanager-pwa · {SENTRY_ENV} · v{APP_VERSION}
          {eventId && ` · ${eventId}`}
        </p>
      </div>
    </div>
  )
}
